import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { configureAbly, useChannel } from '@ably-labs/react-hooks';
import { Types } from 'ably';
import { useDispatch } from 'react-redux';
import { addMessage } from '~/store/messages';
import { addActivities } from '~/store/activities';
import { ActivityRecord, addActivityRecord } from '~/store/activityRecords';

import { useSession } from '~/components/contexts/SessionProvider';
import { Message } from '~/schemas/message';
import { ActivityResponseObject } from '~/schemas/conversation';
import { Conversation } from '~/types';
import { useLayoutContext } from '~/components/layout/Layout';
import notificationSound from '~/assets/notification-sound.wav';
import { useTranslation } from 'react-i18next';

type UnifiedAblyPayload = {
  trigger: 'message' | 'notification';
  messages?: Message[];
  notificationType?: 'manual_mode_message' | 'tags' | string;
  notificationData?: any;
  activity?: ActivityResponseObject;
  conversations?: Conversation[];
};

// Configure Ably (runs once)
// Ensure VITE_API_URL is correctly set in your .env file
const ablyAuthUrl = `${import.meta.env.VITE_API_URL}/realtime/auth`;
configureAbly({ authUrl: ablyAuthUrl });

interface RealtimeContextProps {
  isConnected: boolean;
}

const RealtimeContext = createContext<RealtimeContextProps | undefined>(
  undefined,
);

export const useRealtime = () => {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
};

interface RealtimeProviderProps {
  children: React.ReactNode;
}

const audio = new Audio(notificationSound);
audio.volume = 0.8;

export const RealtimeProvider: React.FC<RealtimeProviderProps> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const { owner } = useSession();
  const [isConnected, setIsConnected] = useState(false);
  const { renderToast } = useLayoutContext();
  const { t } = useTranslation('navigation');

  const userChannelName = owner?._id ? `user-${owner._id}` : null;

  const handleAblyMessage = useCallback(
    (ablyMessage: Types.Message) => {
      try {
        const payload: UnifiedAblyPayload =
          typeof ablyMessage.data === 'string'
            ? JSON.parse(ablyMessage.data)
            : (ablyMessage.data as UnifiedAblyPayload);

        if (!payload || !payload.trigger) {
          console.warn('Received Ably message with invalid payload', payload);
          return;
        }

        switch (payload.trigger) {
          case 'message':
            if (payload.messages && payload.messages.length > 0) {
              payload.messages.forEach((message) => {
                if (message.conversation_id) {
                  dispatch(
                    addMessage({
                      conversationId: message.conversation_id,
                      message: message,
                    }),
                  );
                } else {
                  console.warn(
                    'Received message trigger with missing conversation_id in a message',
                    message,
                  );
                }
              });
            } else {
              console.warn(
                'Received message trigger without messages array',
                payload,
              );
            }

            if (payload.activity && payload.activity._id) {
              dispatch(
                addActivities({
                  activities: [payload.activity],
                  agentId: payload.activity.agent_id,
                }),
              );
            }

            if (payload.conversations && payload.conversations.length > 0) {
              payload.conversations.forEach((conv) => {
                // Map Conversation to Partial<ActivityRecord>
                const activityRecordData: Partial<ActivityRecord> = {
                  ...conv,
                  _id: conv._id, // Ensure _id is passed
                  created_at: conv.created_at, // Already a string
                  updated_at: conv.updated_at, // Already a string
                  ended_at: conv.ended_at || '', // Use ended_at if available or empty string
                  // Map other potentially incompatible fields if necessary
                };
                dispatch(addActivityRecord(activityRecordData));
              });
            }
            break;
          case 'notification':
            audio
              .play()
              .catch((error) => console.error('Error playing sound:', error));

            let toastMessage = t('new_notification_received');
            let toastType: 'success' | 'error' | 'warning' = 'success';

            if (
              payload.notificationType === 'manual_mode_message' &&
              payload.notificationData?.messages?.length > 0
            ) {
              toastMessage = t('new_message_received');
              toastType = 'success';
            } else if (
              payload.notificationType === 'tags' &&
              payload.conversations &&
              payload.conversations.length > 0 &&
              payload.conversations[0]?.tags &&
              payload.conversations[0].tags.length > 0
            ) {
              const tagName = payload.conversations[0].tags[0]?.name || 'new';
              toastMessage = t('new_tag_received', { tag: tagName });
              toastType = 'success';
            }

            renderToast({ message: toastMessage, type: toastType });

            break;
          default:
            break;
        }
      } catch (error: any) {
        console.error(
          'Error processing Ably message:',
          error,
          'Raw data:',
          ablyMessage.data,
        );
      }
    },
    [dispatch, renderToast, t],
  );

  const [channel] = useChannel(userChannelName ?? '', handleAblyMessage);

  useEffect(() => {
    if (!userChannelName) {
      setIsConnected(false);
      return;
    }
    if (!channel) {
      setIsConnected(false);
      return;
    }

    const handleStateChange = (stateChange: Types.ChannelStateChange) => {
      setIsConnected(stateChange.current === 'attached');
      if (
        stateChange.current === 'failed' ||
        stateChange.current === 'suspended'
      ) {
        console.error('Ably channel error state:', stateChange);
      }
    };

    channel.subscribe('unified_update', handleAblyMessage);
    channel.on(handleStateChange);
    setIsConnected(channel.state === 'attached');

    return () => {
      if (channel) {
        channel.unsubscribe('unified_update', handleAblyMessage);
        channel.off(handleStateChange);
      }
    };
  }, [userChannelName, channel, handleAblyMessage]);

  return (
    <RealtimeContext.Provider value={{ isConnected }}>
      {children}
    </RealtimeContext.Provider>
  );
};
